<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Flow
      </h1>
      <p class="page-title__sub">
        Utility classes to control the placement of grid items.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.flow-row</code>
              </td>
              <td>
                <code class="code__prop">grid-auto-flow: row;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.flow-col</code>
              </td>
              <td>
                <code class="code__prop">grid-auto-flow: column;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.flow-row-dense</code>
              </td>
              <td>
                <code class="code__prop">grid-auto-flow: row dense;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.flow-col-dense</code>
              </td>
              <td>
                <code class="code__prop">grid-auto-flow: col dense;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Flow row
        </h2>
      </header>
      <p>
        To place grid items horizontally use,
        <code>.flow-row</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid flow-row grid-cols-6..."&gt;
  &lt;div class="item"&gt;1&lt;div&gt;
  ...
  &lt;div class="item"&gt;12&lt;div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 gap
          l-gap m-gap sm-gap align-items-start width-full"
          >
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              1
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              2
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              3
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              4
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              5
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              6
            </span>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Flow column
        </h2>
      </header>
      <p>
        To place grid items vertically in columns use,
        <code>.flow-col</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid flow-col grid-cols-6..."&gt;
  &lt;div class="item"&gt;1&lt;div&gt;
  ...
  &lt;div class="item"&gt;12&lt;div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid flow-col grid-cols-3 grid-rows-2 gap
          l-gap m-gap sm-gap align-items-start width-full"
          >
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              1
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              2
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              3
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              4
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              5
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              6
            </span>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Flow dense
        </h2>
      </header>
      <p>
        Using flow dense with either rows or columns will attempt to fill spaces
        within the grid, this can be achieved by using either
        <code class="margin-right-5">.flow-col-dense</code> or <code>.flow-row-dense</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid flow-col-dense grid-cols-6..."&gt;
  &lt;div class="item"&gt;1&lt;div&gt;
  ...
  &lt;div class="item"&gt;12&lt;div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid flow-col-dense grid-cols-3 grid-rows-3 gap
          l-gap m-gap sm-gap align-items-start width-full"
          >
            <span class="preview__button col-span-2 bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              1
            </span>
            <span class="preview__button col-span-2 bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              2
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              3
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              4
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              5
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              6
            </span>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'Flow',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>

<style scoped>

</style>
